import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { Id, toast } from 'react-toastify'
import i18n from '../../i18n'
import { mapValuesToSelectOptions } from '../../utils/formUtils/forms'
import { updateFile } from '../globalFunctions'
import { DeleteMultipleResponse, PageListParams, UpdateFileResponse } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import { deleteDocumentById, deleteMultipleDocuments, fetchDocumentValues, getDocuments, syncDocumentById, uploadDocument } from './functions'
import { DocumentActionResponse, DocumentCriteria, DocumentList, DocumentValuesData, UpdateDocumentParams, UploadDocumentParams } from './types'

const UPLOAD_ERROR_FIELD_ID_KEYS: Record<string, string> = {
    project: 'app.common.projectId',
    property: 'app.common.propertyId',
    contact: 'form.project.documents.contact',
}

const documents = {
    GetDocuments: {
        useQuery: (
            parentId: number | string | undefined,
            params: PageListParams<DocumentCriteria>,
            options?: UseQueryOptions<DocumentList, AxiosError>
        ) =>
            useQuery({
                queryKey: ['documents', 'list', parentId, params],
                queryFn: () => getDocuments(parentId as string | number, params),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    UploadDocument: {
        useMutation: (options?: UseMutationOptions<unknown, AxiosError<{ error?: string }>, UploadDocumentParams, Id>) =>
            useMutation({
                ...options,
                mutationFn: (params) => uploadDocument(params),
                onMutate: () => toast.loading(i18n.t('toast.loading.documents') as string),
                onSuccess: (_data, _params, toastId) => {
                    if (toastId) updateToast(toastId, i18n.t('toast.update.documents.success') as string, 'success')

                    invalidateQueries(['documents', 'list'])
                },
                onError: (error, _params, toastId) => {
                    const errorMessage = error?.response?.data?.error || ''
                    let toastMessage = i18n.t('toast.update.documents.error') as string

                    if (errorMessage.includes("\\'")) {
                        const entity = errorMessage.split("\\'")[1]
                        const key = i18n.t(UPLOAD_ERROR_FIELD_ID_KEYS[entity] ?? UPLOAD_ERROR_FIELD_ID_KEYS.project)

                        toastMessage = i18n.t('toast.update.documents.error.fieldId', { key }) as string
                    }

                    if (toastId) updateToast(toastId, toastMessage, 'error')
                },
            }),
    },
    UpdateDocument: {
        useMutation: (options?: UseMutationOptions<UpdateFileResponse, AxiosError, UpdateDocumentParams>) =>
            useMutation({
                ...options,
                mutationFn: (params) => {
                    const {
                        id,
                        document_type_id,
                        document_creation_date,
                        document_validity_date,
                        file_description,
                        file_sync,
                        parent,
                        parentInternalAppId,
                    } = params
                    const locale = localStorage.getItem('i18nextLng') || 'hr'

                    return updateFile({
                        id,
                        document_type_id,
                        document_creation_date,
                        document_validity_date,
                        file_sync,
                        parent,
                        parent_internal_app_id: parentInternalAppId,
                        translations: [{ locale, fileDescription: file_description }],
                    })
                },
                onMutate: (params) => {
                    toast.loading(i18n.t('toast.loading') as string, { toastId: Number(params.id) })
                },
                onSuccess: (_data, params) => {
                    updateToast(Number(params.id), i18n.t('toast.update.success') as string, 'success')
                    invalidateQueries(['documents', 'list'])
                },
                onError: (_error, params) => {
                    updateToast(Number(params.id), i18n.t('toast.update.error') as string, 'error')
                },
            }),
    },
    DeleteDocument: {
        useMutation: (options?: UseMutationOptions<DocumentActionResponse, AxiosError, number>) =>
            useMutation({
                ...options,
                mutationFn: (id) => deleteDocumentById(Number(id)),
                onMutate: (id) => {
                    toast.loading(i18n.t('toast.loading.delete') as string, { toastId: Number(id) })
                },
                onSuccess: (data) => {
                    updateToast(Number(data.id), i18n.t('toast.update.delete.success') as string, 'success')
                    invalidateQueries(['documents', 'list'])
                },
                onError: (_error, id) => updateToast(Number(id), i18n.t('toast.update.delete.error') as string, 'error'),
            }),
    },
    DeleteMultipleDocuments: {
        useMutation: (options?: UseMutationOptions<DeleteMultipleResponse, AxiosError, number[]>) =>
            useMutation({
                ...options,
                mutationFn: (arrayId) => deleteMultipleDocuments(arrayId),
                onMutate: (idsArray) => {
                    const requestsToBeDeletedCount = idsArray.length
                    toast.loading(i18n.t('toast.loading.delete.document.number', { requestsToBeDeletedCount }) as string, {
                        toastId: idsArray[0].toString(),
                    })
                },
                onSuccess: (data, idsArray) => {
                    const requestsToBeDeletedCount = idsArray?.length
                    const failedCount = data?.notFoundIds?.length
                    const successCount = data?.successfulDelete?.length

                    const successMessage = i18n.t('toast.loading.delete.document.number.success', {
                        successCount,
                        requestsToBeDeletedCount,
                    }) as string
                    const partialErrorMessage = i18n.t('toast.loading.delete.document.number.error', {
                        failedCount,
                        requestsToBeDeletedCount,
                    }) as string

                    if (failedCount > 0) {
                        updateToast(idsArray[0].toString(), partialErrorMessage, 'error')
                    }

                    if (successCount > 0) {
                        updateToast(idsArray[0].toString(), successMessage, 'success')
                        invalidateQueries(['documents', 'list'])
                    }
                },
                onError: (error, idsArray) => {
                    updateToast(idsArray[0].toString(), i18n.t('toast.update.delete.error') as string, 'error')
                    console.error(error)
                },
            }),
    },
    SyncDocument: {
        useMutation: (options?: UseMutationOptions<DocumentActionResponse, AxiosError, number>) =>
            useMutation({
                ...options,
                mutationFn: (id) => syncDocumentById(Number(id)),
                onMutate: (id) => {
                    toast.loading(i18n.t('toast.loading') as string, { toastId: Number(id) })
                },
                onSuccess: (data) => {
                    updateToast(Number(data?.id), i18n.t('toast.update.success') as string, 'success')
                    invalidateQueries(['documents', 'list'])
                },
                onError: (_error, id) => updateToast(Number(id), i18n.t('toast.update.error') as string, 'error'),
            }),
    },
    FormValues: {
        useQuery: (fields?: string[], options?: Omit<UseQueryOptions<DocumentValuesData, AxiosError>, 'queryKey' | 'queryFn'>) => {
            const query = useQuery({
                queryKey: ['documentValues', fields],
                queryFn: () => fetchDocumentValues(fields),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            })

            const formattedData = useMemo(() => {
                return mapValuesToSelectOptions(query.data)
            }, [query.data])

            return {
                ...query,
                data: formattedData,
            }
        },
    },
}

export default documents
